import {
  ReturnTableFilterNames,
  returnTableFilterData,
} from "@redotech/redo-return-core-common/return-search-filters";
import { ReturnTypeEnum } from "@redotech/redo-return-core-common/return-type-enum";
import {
  NeedsActionEntry,
  needsActionEntryFilters,
} from "@redotech/redo-return-operations-common/views/needs-action";
import { AdvancedFilterData } from "@redotech/redo-views-common/advanced-filters/advanced-filter-data";
import { GlobalSearchCategory } from "@redotech/redo-views-common/global-search/global-search-category";
import type { JSXElementConstructor } from "react";
import { CreatableRecordType } from "../../record-create/record-create-actions";
import { recordListHref } from "../../record-links/record-links";
import type { BuiltInRecordView } from "../../views/use-record-view-catalog";
import { RecordViewType } from "../../views/use-record-view-counts";
import type {
  HomeRecordType,
  HomeRoutes,
  HomeT,
  NeedsActionCard,
  RecordHomeCopy,
  RecordHomeSource,
  SearchFilter,
} from "./record-home-source";

export type CardCopy = Pick<NeedsActionCard, "tone" | "copy" | "processText">;

const NEEDS_ACTION_FILTER: AdvancedFilterData =
  returnTableFilterData[ReturnTableFilterNames.NEEDS_ACTION];

/** A source for a type stored as a return record; every card counts and links by its view's filters. */
export function returnRecordSource({
  type,
  recordType,
  Icon,
  availability,
  viewType,
  countSavedViews,
  category,
  tableFilters,
  extraFilters,
  pinNeedsAction = false,
  quickViews = [],
  entries,
  cardCopy,
  copy,
  create,
}: {
  type: HomeRecordType;
  recordType: ReturnTypeEnum;
  Icon: JSXElementConstructor<object>;
  availability: RecordHomeSource["availability"];
  viewType: RecordViewType;
  countSavedViews: boolean;
  category: GlobalSearchCategory;
  tableFilters: RecordHomeSource["search"]["tableFilters"];
  extraFilters?(t: HomeT): readonly SearchFilter[];
  /** Offers the needs-action records as a quick view, ahead of `quickViews`. */
  pinNeedsAction?: boolean;
  quickViews?: readonly BuiltInRecordView[];
  entries: readonly NeedsActionEntry[];
  /** Keyed by entry id; an entry without copy gets no card. */
  cardCopy: Readonly<Record<string, CardCopy>>;
  copy(t: HomeT): RecordHomeCopy;
  create: CreatableRecordType;
}): RecordHomeSource {
  const filtersHref: RecordHomeSource["filtersHref"] = (routes, filters) =>
    recordListHref(routes.returnsBaseUrl, recordType, { filters });
  const needsActionHref = (routes: HomeRoutes) =>
    filtersHref(routes, [NEEDS_ACTION_FILTER]);

  const cards = entries.flatMap((entry): NeedsActionCard[] => {
    const filters = needsActionEntryFilters(recordType, entry);
    const entryCopy = cardCopy[entry.id];
    if (!filters || !entryCopy) {
      return [];
    }
    return [
      {
        entry,
        filters,
        ...entryCopy,
        href: (routes) => filtersHref(routes, filters),
      },
    ];
  });

  return {
    type,
    recordType,
    Icon,
    availability,
    copy,
    search: { category, returnType: recordType, tableFilters, extraFilters },
    views: { viewType, counted: countSavedViews },
    quickViews: [
      ...(pinNeedsAction
        ? [
            {
              id: "needs-action",
              label: (t: HomeT) => t("Needs action"),
              filters: [NEEDS_ACTION_FILTER],
              href: needsActionHref,
              urgent: true,
            },
          ]
        : []),
      ...quickViews,
    ],
    allHref: (routes) => recordListHref(routes.returnsBaseUrl, recordType),
    searchHref: (routes, query) =>
      recordListHref(routes.returnsBaseUrl, recordType, { search: query }),
    filtersHref,
    needsAction: {
      filters: [NEEDS_ACTION_FILTER],
      href: needsActionHref,
      cards,
    },
    create,
  };
}
